/**
 * The one label for each submission status. Every list, chip, status bar and the Change Log
 * read it, so a status reads the same wherever it shows.
 */
const SUBMISSION_STATUS_LABELS: Readonly<Record<string, string>> = {
  INCOMPLETE: 'In Process',
  PROCESSED: 'Quoted',
  PENDING: 'Quote Pending',
  SCHEDULED: 'Scheduled',
  DECLINED: 'Declined',
  REFERRAL: 'Referred',
  CARRIER_REFERRAL: 'Referred to Carrier',
  LOST: 'Lost',
  NON_RENEWED: 'Non-Renewed',
  BOUND: 'Bound',
  RENEWED: 'Renewed',
  MIGRATED: 'Migrated',
  NEEDS_APP: 'Needs App',
  BIND_ORDER_RECEIVED: 'Bind Order Received',
  DISCARDED: 'Discarded',
  INVALID: 'Invalid',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
  EXTENDED: 'Extended',
};

const titleCase = (status: string): string =>
  status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const submissionStatusLabel = (status: string | undefined): string => {
  if (!status) {
    return '';
  }
  return SUBMISSION_STATUS_LABELS[status.toUpperCase()] ?? titleCase(status);
};

interface StatusOption {
  readonly label: string;
  readonly value: string;
}

/**
 * Status filter options with the shared labels. Statuses that share a label become one option whose value lists
 * them all, so the filter shows each label once and still matches every status behind it.
 */
export const relabelStatusOptions = (
  options: readonly StatusOption[] | undefined
): StatusOption[] => {
  const valuesByLabel = new Map<string, string[]>();
  (options ?? []).forEach(({ value }) => {
    const label = submissionStatusLabel(value);
    valuesByLabel.set(label, [...(valuesByLabel.get(label) ?? []), value]);
  });
  return Array.from(valuesByLabel, ([label, values]) => ({
    label,
    value: values.join(','),
  }));
};
